// Camera capture screen.
//
// Flow:
//   1. If the profile requires a subject (rash → site) and none is set, render
//      the subject picker first; navigate to the resolved profile id once picked.
//   2. Request rear camera at max resolution (mediaDevices.getUserMedia).
//   3. Show live preview + per-profile framing overlay + scale-ref / lighting controls.
//   4. On shutter: grab full-res JPEG (ImageCapture API if available; canvas fallback).
//   5. Freeze the frame and show Keep / Retake controls.
//   6. On Keep: save photo + metadata to storage, navigate home.
//
// Returns a cleanup() function that stops the MediaStream — the router calls
// it when leaving the route so the camera is never left running.

import { getProfile, RASH_SITES, sanitiseSubject, composeProfileId } from './profiles.js';
import { SCALE_REFS, resolveScaleObject } from './scale-refs.js';
import { LIGHTING_OPTIONS } from './lighting.js';
import { savePhoto, buildPhotoFilename } from '../storage/storage.js';
import { toast, esc } from './util.js';

export function renderCapture(app, profileId) {
  const profile = getProfile(profileId);
  if (!profile) {
    toast(`Unknown profile: ${profileId}`, { kind: 'error' });
    location.hash = '#/';
    return null;
  }

  // Subject-bearing profile (e.g. rash) with no subject yet → render picker.
  if (profile.requiresSubject && !profile.subject) {
    return renderSubjectPicker(app, profile);
  }

  return renderCameraStage(app, profile);
}

// ---- Subject picker (rash site) ----

function renderSubjectPicker(app, profile) {
  app.innerHTML = `
    <div class="subject-picker">
      <header class="sp-header">
        <a id="sp-back" href="#/" class="link-btn">← Cancel</a>
        <h1>Where is the rash?</h1>
      </header>
      <div class="sp-body">
        <div class="sp-grid">
          ${RASH_SITES.map((s) => `
            <button class="sp-pill" data-site="${esc(s.id)}">${esc(s.label)}</button>
          `).join('')}
        </div>
        <div class="sp-other">
          <label class="field">
            <span>Or describe the site</span>
            <input id="sp-custom" type="text" maxlength="24" placeholder="e.g. inside of elbow" />
          </label>
          <button id="sp-custom-go" class="btn btn--primary">Continue</button>
        </div>
      </div>
    </div>
  `;

  const go = (site) => {
    const clean = sanitiseSubject(site);
    if (!clean) {
      toast('Enter a site name', { kind: 'warn' });
      return;
    }
    location.hash = `#/capture/${encodeURIComponent(composeProfileId(profile.baseId ?? profile.id, clean))}`;
  };

  app.querySelectorAll('.sp-pill').forEach((btn) =>
    btn.addEventListener('click', () => go(btn.dataset.site))
  );
  document.getElementById('sp-custom-go').addEventListener('click', () => {
    go(document.getElementById('sp-custom').value);
  });
  document.getElementById('sp-custom').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') go(document.getElementById('sp-custom').value);
  });

  return null;
}

// ---- Camera stage (existing flow) ----

function renderCameraStage(app, profile) {
  app.innerHTML = buildHTML(profile);

  const els = {
    video:        document.getElementById('cam-video'),
    frozen:       document.getElementById('cam-frozen'),
    overlay:      document.getElementById('cam-overlay'),
    scaleGhost:   document.getElementById('cam-scale-ghost'),
    controls:     document.getElementById('cam-controls'),
    error:        document.getElementById('cam-error'),
    back:         document.getElementById('cam-back'),
    shutter:      document.getElementById('cam-shutter'),
    scale:        document.getElementById('cap-scale'),
    customMm:     document.getElementById('cap-custom-mm'),
    customWrap:   document.getElementById('cap-custom-wrap'),
    scaleConfirm: document.getElementById('cap-scale-confirm'),
    scaleWrap:    document.getElementById('cap-scale-wrap'),
    confirmWrap:  document.getElementById('cap-confirm-wrap'),
    lighting:     document.getElementById('cap-lighting'),
    retake:       document.getElementById('cam-retake'),
    keep:         document.getElementById('cam-keep'),
  };

  let stream = null;
  let frozenBlob = null;
  let frozenUrl = null;
  let cancelled = false;

  const cleanup = () => {
    cancelled = true;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    if (frozenUrl) {
      URL.revokeObjectURL(frozenUrl);
      frozenUrl = null;
    }
  };

  els.back.addEventListener('click', (e) => {
    e.preventDefault();
    location.hash = '#/';
  });

  if (profile.requiresScale) {
    els.scale.addEventListener('change', () => {
      els.customWrap.hidden = els.scale.value !== 'custom';
      updateScaleGhost(els.scaleGhost, profile, els.scale.value, els.customMm.value);
    });
    els.customMm.addEventListener('input', () => {
      if (els.scale.value === 'custom') {
        updateScaleGhost(els.scaleGhost, profile, 'custom', els.customMm.value);
      }
    });
  }

  els.shutter.addEventListener('click', async () => {
    if (profile.requiresScale && !els.scaleConfirm.checked) {
      flash(els.scaleConfirm.closest('.confirm-row'));
      toast('Confirm the scale reference is in frame', { kind: 'warn' });
      return;
    }
    if (profile.requiresScale && els.scale.value === 'custom') {
      const mm = Number(els.customMm.value);
      if (!Number.isFinite(mm) || mm <= 0) {
        toast('Enter the custom reference size in mm', { kind: 'warn' });
        els.customMm.focus();
        return;
      }
    }
    els.shutter.disabled = true;
    try {
      frozenBlob = await grabPhoto(stream, els.video);
      frozenUrl = URL.createObjectURL(frozenBlob);
      els.frozen.src = frozenUrl;
      els.frozen.hidden = false;
      els.video.hidden = true;
      els.controls.dataset.mode = 'preview';
    } catch (err) {
      console.error(err);
      toast(`Capture failed: ${err.message}`, { kind: 'error' });
    } finally {
      els.shutter.disabled = false;
    }
  });

  els.retake.addEventListener('click', () => {
    if (frozenUrl) URL.revokeObjectURL(frozenUrl);
    frozenBlob = null;
    frozenUrl = null;
    els.frozen.hidden = true;
    els.frozen.src = '';
    els.video.hidden = false;
    els.controls.dataset.mode = 'live';
  });

  els.keep.addEventListener('click', async () => {
    if (!frozenBlob) return;
    els.keep.disabled = true;
    try {
      const takenAt = new Date();
      const { date, file } = buildPhotoFilename({ takenAt, profile: profile.id });
      const scaleObject = profile.requiresScale
        ? resolveScaleObject(els.scale.value, els.customMm?.value)
        : null;

      await savePhoto(frozenBlob, {
        file,
        date,
        profile: profile.id,
        scaleObject,
        hasScaleReference: profile.requiresScale && !!scaleObject && els.scaleConfirm.checked,
        lighting: els.lighting.value,
        tags: [],
        takenAt: takenAt.toISOString(),
      });

      toast(`Saved ${profile.id}`);
      location.hash = '#/';
    } catch (err) {
      console.error(err);
      toast(`Save failed: ${err.message}`, { kind: 'error' });
      els.keep.disabled = false;
    }
  });

  (async () => {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width:  { ideal: 4096 },
          height: { ideal: 3072 },
        },
        audio: false,
      });
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      els.video.srcObject = stream;
      await els.video.play().catch(() => { /* autoplay attribute usually handles it */ });
      drawOverlay(els.overlay, profile);
      if (profile.requiresScale) {
        updateScaleGhost(els.scaleGhost, profile, els.scale.value, els.customMm.value);
      }
    } catch (err) {
      console.error('Camera failed:', err);
      showCameraError(els, err);
    }
  })();

  return cleanup;
}

// ---- HTML / drawing helpers ----

function buildHTML(profile) {
  const scaleOptions = SCALE_REFS
    .map((s) => `<option value="${esc(s.id)}"${s.defaultRound ? ' selected' : ''}>${esc(s.label)}</option>`)
    .join('');
  const lightingOptions = LIGHTING_OPTIONS
    .map((l) => `<option value="${esc(l.id)}">${esc(l.label)}</option>`)
    .join('');

  // For profiles without scale (face, eye, body), hide scale-ref controls.
  const showScale = profile.requiresScale;

  return `
    <div class="capture-screen">
      <div class="capture-stage">
        <video id="cam-video" autoplay playsinline muted></video>
        <img id="cam-frozen" alt="" hidden />
        <svg id="cam-overlay" class="cam-overlay" preserveAspectRatio="none" viewBox="0 0 100 100"></svg>
        <div id="cam-scale-ghost" class="scale-ghost" hidden></div>
        <div class="cam-header">
          <a id="cam-back" href="#/" class="icon-btn" aria-label="Back">‹ Back</a>
          <div class="cam-title">
            <div class="cam-title__profile">${esc(profile.label)}${profile.subject ? ` — ${esc(profile.subject)}` : ''}</div>
            <div class="cam-title__instruction">${esc(profile.instruction)}</div>
          </div>
        </div>
        <div id="cam-error" class="cam-error" hidden></div>
      </div>

      <div id="cam-controls" class="cam-controls" data-mode="live">
        <div class="cam-controls__live">
          <div class="ctrl-row">
            <label for="cap-lighting">Lighting</label>
            <select id="cap-lighting">${lightingOptions}</select>
          </div>
          <div class="ctrl-row" id="cap-scale-wrap" ${showScale ? '' : 'hidden'}>
            <label for="cap-scale">Scale reference</label>
            <select id="cap-scale">${scaleOptions}</select>
          </div>
          <div class="ctrl-row" id="cap-custom-wrap" hidden>
            <label for="cap-custom-mm">Custom size (mm)</label>
            <input id="cap-custom-mm" type="number" inputmode="decimal" min="1" step="0.1" placeholder="e.g. 24.5" />
          </div>
          <label class="confirm-row" id="cap-confirm-wrap" ${showScale ? '' : 'hidden'}>
            <input id="cap-scale-confirm" type="checkbox" />
            <span>Scale reference is in frame, same plane as the limb</span>
          </label>
          <button id="cam-shutter" class="shutter" aria-label="Capture photo">
            <span class="shutter__ring"></span>
          </button>
        </div>
        <div class="cam-controls__preview">
          <button id="cam-retake" class="btn btn--secondary">Retake</button>
          <button id="cam-keep" class="btn btn--primary">Keep photo</button>
        </div>
      </div>
    </div>
  `;
}

function drawOverlay(svg, profile) {
  const { x, y, w, h } = profile.frame;
  const px = (x * 100).toFixed(2);
  const py = (y * 100).toFixed(2);
  const pw = (w * 100).toFixed(2);
  const ph = (h * 100).toFixed(2);
  svg.innerHTML = `
    <rect x="${px}" y="${py}" width="${pw}" height="${ph}"
          fill="none" stroke="rgba(255,255,255,0.85)" stroke-width="0.4"
          stroke-dasharray="1.5 1" vector-effect="non-scaling-stroke" />
    <text x="50" y="${(y * 100 - 1).toFixed(2)}" text-anchor="middle"
          fill="rgba(255,255,255,0.85)" font-size="2.4"
          font-family="system-ui, sans-serif">framing guide</text>
  `;
}

/**
 * Position + size the scale-reference silhouette on the camera view so it
 * shows the user where (and at what size) to place the chosen coin or grid
 * for consistent framing across days.
 *
 * Sizing: `(refDimMm / expectedFrameMm.w) × frame.w × 100%` of viewport width.
 * For a 25.9 mm 2p on a 70 mm expected hand-back frame (w=0.70 of viewport),
 * that's ~26% of viewport width — visually substantial without crowding.
 *
 * Placement: tries to sit OUTSIDE the framing rectangle (below if there's
 * vertical room, otherwise to the right, otherwise bottom-right corner).
 */
function updateScaleGhost(host, profile, scaleId, customMmRaw) {
  if (!host || !profile?.requiresScale) {
    if (host) host.hidden = true;
    return;
  }
  const ref = SCALE_REFS.find((r) => r.id === scaleId);
  if (!ref) { host.hidden = true; return; }

  let dimMm = ref.dimMm;
  if (scaleId === 'custom') dimMm = Number(customMmRaw);
  if (!Number.isFinite(dimMm) || dimMm <= 0) { host.hidden = true; return; }

  const expectedMm = profile.expectedFrameMm?.w ?? 70;
  const frameW = profile.frame.w;

  // Reset inline styles before reapplying.
  host.style.cssText = '';
  host.classList.remove('scale-ghost--coin', 'scale-ghost--grid', 'scale-ghost--custom');

  let widthPct;
  let labelText;
  if (ref.shape === 'circle') {
    widthPct = (dimMm / expectedMm) * frameW * 100;
    host.classList.add('scale-ghost--coin');
    labelText = ref.label;
  } else if (ref.shape === 'grid') {
    // Show a 2×2 grid (20 mm × 20 mm of 10 mm cells) — enough to demonstrate
    // calibration without dominating the frame.
    const gridMm = 20;
    widthPct = (gridMm / expectedMm) * frameW * 100;
    host.classList.add('scale-ghost--grid');
    labelText = '1 cm grid';
  } else {
    widthPct = (dimMm / expectedMm) * frameW * 100;
    host.classList.add('scale-ghost--custom');
    labelText = `${dimMm} mm`;
  }

  // Clamp so it doesn't dominate the viewport on very small / very large refs.
  widthPct = Math.max(8, Math.min(40, widthPct));

  // Placement: outside the framing box if possible.
  const boxBottom = (profile.frame.y + profile.frame.h) * 100;
  const boxRight  = (profile.frame.x + profile.frame.w) * 100;
  const belowH = 100 - boxBottom;
  const rightW = 100 - boxRight;

  host.style.width = `${widthPct.toFixed(2)}%`;
  if (belowH >= 14) {
    // Place centred under the framing box.
    host.style.left = `${((profile.frame.x + profile.frame.w / 2) * 100).toFixed(2)}%`;
    host.style.top  = `${(boxBottom + 2).toFixed(2)}%`;
    host.style.transform = 'translateX(-50%)';
  } else if (rightW >= 14) {
    // Place to the right of the framing box, vertically centred.
    host.style.left = `${(boxRight + 2).toFixed(2)}%`;
    host.style.top  = `${((profile.frame.y + profile.frame.h / 2) * 100).toFixed(2)}%`;
    host.style.transform = 'translateY(-50%)';
  } else {
    // Fallback: bottom-right corner of viewport (may overlap framing).
    host.style.right = '4%';
    host.style.bottom = '4%';
  }

  host.innerHTML = `<span class="scale-ghost__label">${esc(labelText)}</span>`;
  host.hidden = false;
}

function showCameraError(els, err) {
  els.error.hidden = false;
  let msg = 'Camera unavailable.';
  if (err && err.name === 'NotAllowedError') {
    msg = 'Camera permission denied. Allow camera access in your browser settings, then reload.';
  } else if (err && err.name === 'NotFoundError') {
    msg = 'No camera found on this device.';
  } else if (err && err.name === 'NotReadableError') {
    msg = 'Camera is in use by another app. Close it and try again.';
  } else if (err && err.message) {
    msg = `Camera error: ${err.message}`;
  }
  els.error.innerHTML = `<p>${esc(msg)}</p><a class="btn btn--secondary" href="#/">Back</a>`;
}

function flash(el) {
  if (!el) return;
  el.classList.remove('flash');
  void el.offsetWidth;
  el.classList.add('flash');
}

async function grabPhoto(stream, videoEl) {
  const track = stream && stream.getVideoTracks()[0];
  if (!track) throw new Error('No active video track');
  if ('ImageCapture' in window) {
    try {
      const ic = new ImageCapture(track);
      const raw = await ic.takePhoto();
      if (raw.type === 'image/jpeg') return raw;
      return await reencodeAsJpeg(raw, 0.95);
    } catch (err) {
      console.warn('ImageCapture.takePhoto failed, using canvas fallback:', err);
    }
  }
  return await videoToJpegBlob(videoEl, 0.95);
}

function videoToJpegBlob(video, quality) {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) throw new Error('Video not ready');
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(video, 0, 0, w, h);
  return new Promise((res, rej) => {
    canvas.toBlob((blob) => (blob ? res(blob) : rej(new Error('canvas.toBlob returned null'))), 'image/jpeg', quality);
  });
}

async function reencodeAsJpeg(srcBlob, quality) {
  const bmp = await createImageBitmap(srcBlob);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  canvas.getContext('2d').drawImage(bmp, 0, 0);
  bmp.close?.();
  return new Promise((res, rej) => {
    canvas.toBlob((blob) => (blob ? res(blob) : rej(new Error('re-encode failed'))), 'image/jpeg', quality);
  });
}
